import { useState, useMemo, useCallback } from 'react';

type SortDirection = 'asc' | 'desc';

export interface UseTableSortResult<T> {
  /** Currently sorted field, or null. */
  sortField: string | null;
  /** Current sort direction. */
  sortDirection: SortDirection;
  /** Call with a field name to toggle sort. */
  onSort: (field: string) => void;
  /** The sorted rows. */
  sortedRows: T[];
}

/**
 * Generic hook that adds client-side column header sorting to any array of objects.
 *
 * @param rows  – source data
 * @param opts  – optional `defaultField` and `defaultDirection`
 */
export function useTableSort<T extends object>(
  rows: T[],
  opts?: { defaultField?: string; defaultDirection?: SortDirection },
): UseTableSortResult<T> {
  const [sortField, setSortField] = useState<string | null>(opts?.defaultField ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    opts?.defaultDirection ?? 'asc',
  );

  const onSort = useCallback(
    (field: string) => {
      setSortField((prev) => {
        if (prev === field) {
          if (sortDirection === 'asc') {
            setSortDirection('desc');
            return field;
          }
          // Already desc → clear sort
          setSortDirection('asc');
          return null;
        }
        setSortDirection('asc');
        return field;
      });
    },
    [sortDirection],
  );

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      const aRaw = (a as Record<string, unknown>)[sortField];
      const bRaw = (b as Record<string, unknown>)[sortField];
      const aVal =
        aRaw === null || aRaw === undefined
          ? ''
          : typeof aRaw === 'string'
            ? aRaw.toLowerCase()
            : aRaw;
      const bVal =
        bRaw === null || bRaw === undefined
          ? ''
          : typeof bRaw === 'string'
            ? bRaw.toLowerCase()
            : bRaw;
      if (aVal === bVal) return 0;
      const cmp = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortField, sortDirection]);

  return { sortField, sortDirection, onSort, sortedRows };
}
